import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Metric,
  formatDate,
} from "@odingard/design-system";
import type { LifecycleTask } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { policyApi } from "../../lib/api";
import { PolicyDemoBanner, PolicyLiveBadge } from "../../components/PolicyMode";

const KIND_LABEL: Record<string, string> = {
  policy_review: "Periodic review",
  exception_expiry: "Exception expiry",
  acknowledgment_deadline: "Acknowledgment deadline",
};

export function PolDashboard() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const state = useAsync(
    async () => {
      const [dashboard, tasks] = await Promise.all([
        policyApi.dashboard(orgId),
        policyApi.tasks(orgId, { status: "open" }),
      ]);
      return { dashboard, tasks };
    },
    [orgId],
  );

  const d = state.data?.dashboard.data.dashboard;
  const tasks = state.data?.tasks.data.tasks ?? [];
  const demoMode = state.data?.dashboard.demoMode ?? false;

  async function runLifecycle() {
    setRunning(true);
    setRunError(null);
    try {
      await policyApi.runLifecycle(orgId);
      state.reload();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "Lifecycle run failed.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Policy AI</h1>
          <p>Policy lifecycle health: reviews, acknowledgments, exceptions and due work.</p>
        </div>
        <div className="ods-row">
          <Link to="/studio/policy-ai/drafts">
            <Button size="sm">Authoring</Button>
          </Link>
          <Button size="sm" variant="secondary" onClick={runLifecycle} disabled={running || !orgId}>
            {running ? "Running…" : "Run lifecycle"}
          </Button>
        </div>
      </div>

      <PolicyDemoBanner show={demoMode} />
      <div style={{ marginBottom: "var(--ods-space-3)" }}>
        <PolicyLiveBadge demoMode={demoMode} />
      </div>
      {runError && <ErrorState description={runError} />}
      {state.loading && <LoadingState label="Loading lifecycle dashboard…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {d && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={d.policies.active} label="Active policies" />
            </div>
            <div className="metric-card">
              <Metric value={d.policies.reviewDueSoon} label="Reviews due soon" tone="var(--ods-color-warning)" />
            </div>
            <div className="metric-card">
              <Metric value={d.policies.reviewOverdue} label="Reviews overdue" tone="var(--ods-color-danger)" />
            </div>
            <div className="metric-card">
              <Metric value={d.tasks.open} label="Open lifecycle tasks" />
            </div>
          </div>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={d.acknowledgments.openCampaigns} label="Open campaigns" />
            </div>
            <div className="metric-card">
              <Metric value={d.acknowledgments.pending} label="Pending acknowledgments" tone="var(--ods-color-warning)" />
            </div>
            <div className="metric-card">
              <Metric value={d.exceptions.approved} label="Approved exceptions" />
            </div>
            <div className="metric-card">
              <Metric value={d.exceptions.expiringSoon} label="Exceptions expiring soon" tone="var(--ods-color-warning)" />
            </div>
          </div>

          <Card>
            <CardHeader
              title="Open lifecycle tasks"
              action={
                <Link to="/studio/policy-ai/acknowledgments">
                  <Button size="sm" variant="secondary">
                    Acknowledgments
                  </Button>
                </Link>
              }
            />
            <CardBody>
              {tasks.length === 0 ? (
                <EmptyState title="No open tasks" description="Run the lifecycle to generate due review, expiry and acknowledgment tasks." />
              ) : (
                <DataTable
                  columns={[
                    {
                      key: "subjectId",
                      header: "Subject",
                      render: (t: LifecycleTask) => <code style={{ fontSize: "var(--ods-text-sm)" }}>{t.subjectId}</code>,
                    },
                    { key: "kind", header: "Kind", render: (t: LifecycleTask) => <Badge tone="neutral">{KIND_LABEL[t.kind] ?? t.kind}</Badge> },
                    { key: "dueAt", header: "Due", render: (t: LifecycleTask) => (t.dueAt ? formatDate(t.dueAt) : "Not set") },
                  ]}
                  rows={tasks}
                  caption="Lifecycle tasks"
                />
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
}
